import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { AppHeader } from '../components/ui/AppHeader';
import {
  subscribeToCatalog,
  subscribeToSiteConfig,
  saveKit,
  createKit,
  deleteKit,
  saveHeroConfig,
  saveSiteSettings,
  seedCatalogFromManifest,
  catalogToManifestJSON,
  subscribeToTestimonials,
  saveTestimonial,
  createTestimonial,
  deleteTestimonial,
} from '../utils/shopFirestore';
import {
  ShopKitModel,
  ShopSiteData,
  ShopHeroConfig,
  ShopSiteSettings,
  ShopTestimonial,
  DEFAULT_HERO,
  DEFAULT_SETTINGS,
  DEFAULT_TESTIMONIAL,
} from '../types/shop';
import { portalAssetDisplayLabel } from '../utils/portalAssetUrl';
import { MediaItem } from '../types';
import { MediaPicker } from '../components/media';
import {
  ArrowLeft,
  Store,
  Package,
  LayoutDashboard,
  Settings,
  ShoppingCart,
  Search,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Tag,
  Download,
  Upload,
  Save,
  Loader2,
  X,
  ExternalLink,
  Copy,
  CheckCircle2,
  Film,
  Image as ImageIcon,
  MessageSquareQuote,
  Star,
  GripVertical,
} from 'lucide-react';
import './ShopManagementPage.css';

// Manifest data for seeding (embedded from the existing catalog)
import { MANIFEST_DATA } from './shopManifestData';

type ActiveTab = 'catalog' | 'hero' | 'settings' | 'testimonials' | 'orders';

const TAGS = ['Barns', 'Entries', 'Pavilion/Pergola', 'Other'];
const VIEWER_URL = 'https://shoptimberframekits.com';

/** Normalize price to display format: $79,999 or $79,999.00 */
function formatPrice(value: string): string {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  // Extract digits and optional decimal part
  const match = trimmed.replace(/,/g, '').match(/^[\$£€]?\s*(\d+(?:\.\d{1,2})?)\s*$/);
  if (match) {
    const num = parseFloat(match[1]);
    if (isNaN(num)) return trimmed;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
  }
  return trimmed; // e.g. "coming soon", "TBD"
}

function resolveGalleryUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const clean = path.replace(/^\//, '');
  if (!clean) return '';
  const encoded = clean.split('/').map((s) => encodeURIComponent(s)).join('/');
  return `${VIEWER_URL}/${encoded}`;
}

function mergeGalleryUrls(kit: ShopKitModel): string[] {
  const urls: string[] = [];
  if (kit.thumbnailUrl) urls.push(kit.thumbnailUrl);
  if (kit.galleryUrls?.length) urls.push(...kit.galleryUrls);
  if (kit.thumbnail && kit.thumbnail.startsWith('http')) urls.push(kit.thumbnail);
  if (kit.thumbnail && !kit.thumbnail.startsWith('http')) urls.push(resolveGalleryUrl(kit.thumbnail));
  if (kit.gallery?.length) kit.gallery.forEach((p) => urls.push(resolveGalleryUrl(p)));
  return Array.from(new Set(urls));
}

function getKitThumbnailUrl(kit: ShopKitModel): string | null {
  if (kit.thumbnailUrl) return kit.thumbnailUrl;
  if (kit.thumbnail?.startsWith('http')) return kit.thumbnail;
  if (kit.thumbnail) return resolveGalleryUrl(kit.thumbnail);
  const merged = mergeGalleryUrls(kit);
  return merged[0] || null;
}

/** Gallery URLs shown in the form (galleryUrls + resolved gallery paths). Same logic as KitEditorModal. */
function getFormGalleryUrls(kit: ShopKitModel): string[] {
  const urls: string[] = [...(kit.galleryUrls || [])];
  const gallery = kit.gallery;
  if (Array.isArray(gallery) && gallery.length > 0) {
    gallery.forEach((p) => {
      const path = typeof p === 'string' ? p : String(p);
      if (!path?.trim()) return;
      const resolved = resolveGalleryUrl(path.trim());
      if (resolved && !urls.includes(resolved)) urls.push(resolved);
    });
  }
  return urls;
}

function getKitCompleteness(kit: ShopKitModel): { hasCover: boolean; hasGallery: boolean; hasModel: boolean } {
  const hasCover = !!getKitThumbnailUrl(kit);
  const formGallery = getFormGalleryUrls(kit);
  const hasGallery = formGallery.length > 0;
  const hasModel = !!(kit.modelUrl || (kit.type === 'cadwork-html' && kit.href?.trim()));
  return { hasCover, hasGallery, hasModel };
}

// ─── Main Component ─────────────────────────────────────────────────────────

const ShopManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ActiveTab>('catalog');
  const [kits, setKits] = useState<ShopKitModel[]>([]);
  const [siteData, setSiteData] = useState<ShopSiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Catalog state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [editingKit, setEditingKit] = useState<ShopKitModel | null>(null);
  const [showNewKit, setShowNewKit] = useState(false);

  // Hero edit state
  const [editHero, setEditHero] = useState<ShopHeroConfig>(DEFAULT_HERO);

  // Settings edit state
  const [editSettings, setEditSettings] = useState<ShopSiteSettings>(DEFAULT_SETTINGS);

  // Testimonials state
  const [testimonials, setTestimonials] = useState<ShopTestimonial[]>([]);

  // Subscribe to data
  useEffect(() => {
    const unsub1 = subscribeToCatalog((data) => {
      setKits(data);
      setLoading(false);
    });
    const unsub2 = subscribeToSiteConfig((data) => {
      setSiteData(data);
      setEditHero({ ...data.hero });
      setEditSettings({ ...data.settings });
    });
    const unsub3 = subscribeToTestimonials((data) => {
      setTestimonials(data);
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  const flash = useCallback((msg: string) => {
    setSaveMsg(msg);
    setTimeout(() => setSaveMsg(''), 3000);
  }, []);

  // ─── Catalog Handlers ───────────────────────────────────────────────────

  const filteredKits = kits.filter((k) => {
    if (filterTag && k.tag !== filterTag) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return k.name.toLowerCase().includes(q) || k.description?.toLowerCase().includes(q);
    }
    return true;
  });

  const handleToggleVisibility = async (kit: ShopKitModel) => {
    await saveKit({ id: kit.id, hidden: !kit.hidden });
  };

  const handleToggleSale = async (kit: ShopKitModel) => {
    await saveKit({ id: kit.id, onSale: !kit.onSale });
  };

  const handleToggleFeatured = async (kit: ShopKitModel) => {
    await saveKit({ id: kit.id, featured: !kit.featured });
  };

  const handleDeleteKit = async (kitId: string) => {
    if (!window.confirm('Permanently delete this kit from the catalog?')) return;
    await deleteKit(kitId);
    flash('Kit deleted');
  };

  const handleSaveKit = async (kit: ShopKitModel) => {
    setSaving(true);
    try {
      if (showNewKit) {
        await createKit({ ...kit, status: 'active', sortOrder: kits.length });
        setShowNewKit(false);
        flash('Kit created');
      } else {
        await saveKit(kit);
        flash('Kit updated');
      }
      setEditingKit(null);
    } finally {
      setSaving(false);
    }
  };

  const handleSeedCatalog = async () => {
    if (!window.confirm('Import existing manifest data into Firestore? This will not overwrite kits that already exist.')) return;
    setSaving(true);
    try {
      const count = await seedCatalogFromManifest(MANIFEST_DATA);
      flash(`Imported ${count} new kit${count !== 1 ? 's' : ''}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExportManifest = () => {
    const json = catalogToManifestJSON(kits);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'manifest.json';
    a.click();
    URL.revokeObjectURL(url);
    flash('manifest.json downloaded');
  };

  // ─── Hero Handlers ──────────────────────────────────────────────────────

  const handleSaveHero = async () => {
    setSaving(true);
    try {
      await saveHeroConfig(editHero, user?.email || '');
      flash('Hero section saved');
    } finally {
      setSaving(false);
    }
  };

  // ─── Settings Handlers ──────────────────────────────────────────────────

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const toSave = {
        ...editSettings,
        stripePublishableKey: (editSettings.stripePublishableKey || '').trim(),
      };
      await saveSiteSettings(toSave, user?.email || '');
      flash('Settings saved');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="sm-page">
      <AppHeader />

      {/* Top Bar */}
      <div className="sm-topbar">
        <div className="sm-topbar-left">
          <button className="sm-back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} />
          </button>
          <div className="sm-topbar-title">
            <h1>Shop Management</h1>
            <div className="sm-topbar-subtitle">Manage shoptimberframekits.com content</div>
          </div>
        </div>
        <div className="sm-topbar-right">
          {saveMsg && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-success, #16a34a)' }}>
              <CheckCircle2 size={14} /> {saveMsg}
            </span>
          )}
          <a
            href={VIEWER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="sm-btn-secondary"
          >
            <ExternalLink size={14} /> View Site
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="sm-tabs">
        <button className={`sm-tab ${activeTab === 'catalog' ? 'active' : ''}`} onClick={() => setActiveTab('catalog')}>
          <Package size={16} /> Catalog
          <span className="sm-tab-badge">{kits.filter((k) => k.status === 'active').length}</span>
        </button>
        <button className={`sm-tab ${activeTab === 'hero' ? 'active' : ''}`} onClick={() => setActiveTab('hero')}>
          <LayoutDashboard size={16} /> Hero
        </button>
        <button className={`sm-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          <Settings size={16} /> Site Settings
        </button>
        <button className={`sm-tab ${activeTab === 'testimonials' ? 'active' : ''}`} onClick={() => setActiveTab('testimonials')}>
          <MessageSquareQuote size={16} /> Testimonials
          <span className="sm-tab-badge">{testimonials.filter((t) => t.status === 'active').length}</span>
        </button>
        <button className={`sm-tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          <ShoppingCart size={16} /> Orders
        </button>
      </div>

      {/* Content */}
      <div className="sm-content">
        {activeTab === 'catalog' && (
          <CatalogTab
            kits={filteredKits}
            allKits={kits}
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterTag={filterTag}
            setFilterTag={setFilterTag}
            onEdit={setEditingKit}
            onToggleVisibility={handleToggleVisibility}
            onToggleSale={handleToggleSale}
            onToggleFeatured={handleToggleFeatured}
            onDelete={handleDeleteKit}
            onNewKit={() => {
              setShowNewKit(true);
              setEditingKit({
                id: '',
                name: '',
                type: 'cadwork-html',
                href: '',
                description: '',
                tag: 'Other',
                price: '',
                features: [],
                gallery: [],
                thumbnail: '',
                sortOrder: kits.length,
                status: 'active',
                createdAt: new Date(),
                updatedAt: new Date(),
              });
            }}
            onSeed={handleSeedCatalog}
            onExport={handleExportManifest}
            saving={saving}
          />
        )}
        {activeTab === 'hero' && (
          <HeroTab
            hero={editHero}
            setHero={setEditHero}
            onSave={handleSaveHero}
            saving={saving}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsTab
            settings={editSettings}
            setSettings={setEditSettings}
            onSave={handleSaveSettings}
            saving={saving}
          />
        )}
        {activeTab === 'testimonials' && (
          <TestimonialsTab
            testimonials={testimonials}
            kits={kits}
            saving={saving}
            setSaving={setSaving}
            flash={flash}
          />
        )}
        {activeTab === 'orders' && <OrdersTab />}
      </div>

      {/* Kit Editor Modal - use latest kit from catalog to avoid stale data */}
      {editingKit && (
        <KitEditorModal
          kit={kits.find((k) => k.id === editingKit.id) ?? editingKit}
          isNew={showNewKit}
          saving={saving}
          onSave={handleSaveKit}
          onClose={() => { setEditingKit(null); setShowNewKit(false); }}
        />
      )}
    </div>
  );
};

// ─── Catalog Tab ────────────────────────────────────────────────────────────

interface CatalogTabProps {
  kits: ShopKitModel[];
  allKits: ShopKitModel[];
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  filterTag: string | null;
  setFilterTag: (t: string | null) => void;
  onEdit: (kit: ShopKitModel) => void;
  onToggleVisibility: (kit: ShopKitModel) => void;
  onToggleSale: (kit: ShopKitModel) => void;
  onToggleFeatured: (kit: ShopKitModel) => void;
  onDelete: (id: string) => void;
  onNewKit: () => void;
  onSeed: () => void;
  onExport: () => void;
  saving: boolean;
}

const CatalogTab: React.FC<CatalogTabProps> = ({
  kits, allKits, loading, searchQuery, setSearchQuery, filterTag, setFilterTag,
  onEdit, onToggleVisibility, onToggleSale, onToggleFeatured, onDelete,
  onNewKit, onSeed, onExport, saving,
}) => {
  if (loading) {
    return (
      <div className="sm-empty">
        <Loader2 size={32} className="animate-spin" />
        <p>Loading catalog...</p>
      </div>
    );
  }

  return (
    <>
      {allKits.length === 0 && (
        <div className="sm-seed-banner">
          <p>
            <strong>Catalog is empty.</strong> Import your existing product data from the manifest to get started.
          </p>
          <button className="sm-btn-primary" onClick={onSeed} disabled={saving}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Import Manifest
          </button>
        </div>
      )}

      <div className="sm-catalog-toolbar">
        <div className="sm-catalog-toolbar-left">
          <div className="sm-search-input">
            <Search size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search kits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            className={`sm-filter-btn ${filterTag === null ? 'active' : ''}`}
            onClick={() => setFilterTag(null)}
          >
            All
          </button>
          {TAGS.map((t) => (
            <button
              key={t}
              className={`sm-filter-btn ${filterTag === t ? 'active' : ''}`}
              onClick={() => setFilterTag(filterTag === t ? null : t)}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="sm-catalog-toolbar-right">
          <button className="sm-btn-secondary" onClick={onExport}>
            <Download size={14} /> Export JSON
          </button>
          {allKits.length > 0 && (
            <button className="sm-btn-secondary" onClick={onSeed} disabled={saving}>
              <Upload size={14} /> Re-Sync
            </button>
          )}
          <button className="sm-btn-primary" onClick={onNewKit}>
            <Plus size={14} /> Add Kit
          </button>
        </div>
      </div>

      {kits.length === 0 ? (
        <div className="sm-empty">
          <Package size={48} />
          <p>No kits match your filters.</p>
        </div>
      ) : (
        <table className="sm-kit-table">
          <thead>
            <tr>
              <th style={{ width: 72 }}>Image</th>
              <th style={{ width: 90 }} title="Cover | Gallery | 3D Model">Info</th>
              <th>Name</th>
              <th>Category</th>
              <th>Price</th>
              <th>Status</th>
              <th>Flags</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {kits.map((kit) => (
              <tr key={kit.id}>
                <td>
                  {getKitThumbnailUrl(kit) ? (
                    <img
                      src={getKitThumbnailUrl(kit)!.replace(/ /g, '%20')}
                      alt={kit.name}
                      className="sm-kit-thumb"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="sm-kit-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Package size={18} style={{ opacity: 0.3 }} />
                    </div>
                  )}
                </td>
                <td>
                  {(() => {
                    const { hasCover, hasGallery, hasModel } = getKitCompleteness(kit);
                    return (
                      <div className="sm-kit-info-checks" title="Cover | Gallery (+1) | 3D Model">
                        <label className="sm-info-check" title={hasCover ? 'Has cover image' : 'Missing cover image'}>
                          <input type="checkbox" checked={hasCover} disabled readOnly />
                          <span>C</span>
                        </label>
                        <label className="sm-info-check" title={hasGallery ? 'Has gallery images' : 'Need at least 1 gallery image'}>
                          <input type="checkbox" checked={hasGallery} disabled readOnly />
                          <span>G</span>
                        </label>
                        <label className="sm-info-check" title={hasModel ? 'Has 3D model' : 'Missing 3D model'}>
                          <input type="checkbox" checked={hasModel} disabled readOnly />
                          <span>3D</span>
                        </label>
                      </div>
                    );
                  })()}
                </td>
                <td>
                  <div className="sm-kit-name">{kit.name}</div>
                  <div className="sm-kit-desc">{kit.description}</div>
                </td>
                <td><span className="sm-badge" style={{ background: 'var(--color-background)' }}>{kit.tag}</span></td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{formatPrice(kit.price)}</div>
                  {kit.originalPrice && kit.onSale && (
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textDecoration: 'line-through' }}>{formatPrice(kit.originalPrice)}</div>
                  )}
                </td>
                <td>
                  <span className={`sm-badge sm-badge-${kit.status}`}>{kit.status}</span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {kit.onSale && <span className="sm-badge sm-badge-sale">Sale</span>}
                    {kit.hidden && <span className="sm-badge sm-badge-hidden">Hidden</span>}
                    {kit.featured && <span className="sm-badge sm-badge-active">Featured</span>}
                  </div>
                </td>
                <td>
                  <div className="sm-kit-actions">
                    <button className="sm-icon-btn" title="Edit" onClick={() => onEdit(kit)}>
                      <Pencil size={15} />
                    </button>
                    <button
                      className="sm-icon-btn"
                      title={kit.hidden ? 'Show' : 'Hide'}
                      onClick={() => onToggleVisibility(kit)}
                    >
                      {kit.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <button
                      className="sm-icon-btn"
                      title={kit.onSale ? 'Remove Sale' : 'Mark Sale'}
                      onClick={() => onToggleSale(kit)}
                    >
                      <Tag size={15} />
                    </button>
                    <button className="sm-icon-btn danger" title="Delete" onClick={() => onDelete(kit.id)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

// ─── Kit Editor Modal ───────────────────────────────────────────────────────

interface KitEditorModalProps {
  kit: ShopKitModel;
  isNew: boolean;
  saving: boolean;
  onSave: (kit: ShopKitModel) => void;
  onClose: () => void;
}

const KitEditorModal: React.FC<KitEditorModalProps> = ({ kit, isNew, saving, onSave, onClose }) => {
  const mergedGallery = getFormGalleryUrls(kit);

  const [form, setForm] = useState<ShopKitModel>(() => ({
    ...kit,
    galleryUrls: mergedGallery.length > 0 ? mergedGallery : (kit.galleryUrls || []),
  }));
  const [featuresText, setFeaturesText] = useState(kit.features.join('\n'));
  const [showThumbPicker, setShowThumbPicker] = useState(false);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);

  React.useEffect(() => {
    const gallery = getFormGalleryUrls(kit);
    setForm({
      ...kit,
      galleryUrls: gallery.length > 0 ? gallery : (kit.galleryUrls || []),
    });
    setFeaturesText(kit.features.join('\n'));
  }, [kit.id]);

  const update = (field: keyof ShopKitModel, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const id = isNew
      ? form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      : form.id;
    const payload = {
      ...form,
      id,
      price: formatPrice(form.price),
      originalPrice: form.originalPrice ? formatPrice(form.originalPrice) : '',
      features: featuresText.split('\n').map((f) => f.trim()).filter(Boolean),
      galleryUrls: form.galleryUrls || [],
      gallery: [], // Clear legacy gallery; we use galleryUrls only
    };
    onSave(payload as ShopKitModel);
  };

  const handleThumbSelect = (item: MediaItem) => {
    update('thumbnailUrl', item.url);
  };

  const handleGalleryAdd = (item: MediaItem) => {
    const current = form.galleryUrls || [];
    update('galleryUrls', [...current, item.url]);
  };

  const handleGalleryAddMultiple = (items: MediaItem[]) => {
    const current = form.galleryUrls || [];
    const newUrls = items.map((i) => i.url).filter((u) => !current.includes(u));
    update('galleryUrls', [...current, ...newUrls]);
  };

  const handleGalleryRemove = (index: number) => {
    const current = form.galleryUrls || [];
    update('galleryUrls', current.filter((_, i) => i !== index));
  };

  const handleGalleryImageError = (url: string) => {
    const current = form.galleryUrls || [];
    update('galleryUrls', current.filter((u) => u !== url));
  };

  const handleModelSelect = (item: MediaItem) => {
    update('modelUrl', item.url);
  };

  return (
    <div className="sm-modal-overlay" onClick={onClose}>
      <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sm-modal-header">
          <h2>{isNew ? 'Add New Kit' : `Edit: ${form.name}`}</h2>
          <button className="sm-icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sm-modal-body">
          <div className="sm-form-grid">
            <div className="sm-form-field">
              <label className="sm-form-label">Name</label>
              <input
                className="sm-form-input"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="e.g. Robinson Barn"
              />
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Category</label>
              <select className="sm-form-select" value={form.tag} onChange={(e) => update('tag', e.target.value)}>
                {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm-form-field full-width">
              <label className="sm-form-label">Description</label>
              <textarea
                className="sm-form-textarea"
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Brief description of this kit..."
              />
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Price</label>
              <input
                className="sm-form-input"
                value={form.price}
                onChange={(e) => update('price', e.target.value)}
                onBlur={(e) => update('price', formatPrice(e.target.value))}
                placeholder="$79,999"
              />
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Original Price (if on sale)</label>
              <input
                className="sm-form-input"
                value={form.originalPrice || ''}
                onChange={(e) => update('originalPrice', e.target.value)}
                onBlur={(e) => update('originalPrice', formatPrice(e.target.value) || undefined)}
                placeholder="$118,999"
              />
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Type</label>
              <select className="sm-form-select" value={form.type} onChange={(e) => update('type', e.target.value)}>
                <option value="cadwork-html">3D Viewer (cadwork)</option>
                <option value="coming-soon">Coming Soon</option>
              </select>
            </div>
            <div className="sm-form-field">
              <label className="sm-form-label">Status</label>
              <select className="sm-form-select" value={form.status} onChange={(e) => update('status', e.target.value as ShopKitModel['status'])}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Thumbnail */}
            <div className="sm-form-field full-width">
              <label className="sm-form-label"><ImageIcon size={13} /> Thumbnail Image</label>
              {form.thumbnailUrl ? (
                <div className="sm-media-preview" style={{ height: 120 }}>
                  <img src={form.thumbnailUrl} alt="Thumbnail" className="sm-media-image" />
                  <div className="sm-media-overlay">
                    <button className="sm-media-change-btn" onClick={() => setShowThumbPicker(true)}>Change</button>
                    <button className="sm-media-remove-btn" onClick={() => update('thumbnailUrl', '')}>Remove</button>
                  </div>
                </div>
              ) : (
                <button className="sm-media-select-btn" style={{ height: 80 }} onClick={() => setShowThumbPicker(true)}>
                  <ImageIcon size={16} />
                  <span>Select Thumbnail from Media Library</span>
                </button>
              )}
              <input
                className="sm-form-input"
                value={form.thumbnail}
                onChange={(e) => update('thumbnail', e.target.value)}
                placeholder="Or enter legacy path: assets/Models/.../Cover.webp"
                style={{ marginTop: 6, fontSize: 11, color: '#999' }}
              />
            </div>

            {/* Gallery */}
            <div className="sm-form-field full-width">
              <label className="sm-form-label"><ImageIcon size={13} /> Gallery Images</label>
              {form.galleryUrls && form.galleryUrls.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {form.galleryUrls.map((url, i) => (
                    <div key={`${url}-${i}`} className="sm-media-preview" style={{ width: 100, height: 75 }}>
                      <img
                        src={url}
                        alt={`Gallery ${i + 1}`}
                        className="sm-media-image"
                        onError={() => handleGalleryImageError(url)}
                      />
                      <div className="sm-media-overlay">
                        <button className="sm-media-remove-btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => handleGalleryRemove(i)}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button className="sm-media-select-btn" style={{ height: 60 }} onClick={() => setShowGalleryPicker(true)}>
                <Plus size={14} />
                <span>Add Gallery Images (select multiple)</span>
              </button>
            </div>

            {/* 3D Model */}
            <div className="sm-form-field full-width">
              <label className="sm-form-label"><Package size={13} /> 3D Model / Viewer</label>
              {form.modelUrl ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--color-surface-secondary)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                  <Package size={16} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={form.modelUrl}>{portalAssetDisplayLabel(form.modelUrl)}</span>
                  <button className="sm-media-change-btn" style={{ fontSize: 10 }} onClick={() => setShowModelPicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" style={{ fontSize: 10, background: 'var(--color-danger)', color: '#fff' }} onClick={() => update('modelUrl', '')}>Remove</button>
                </div>
              ) : (
                <button className="sm-media-select-btn" style={{ height: 60 }} onClick={() => setShowModelPicker(true)}>
                  <Package size={16} />
                  <span>Select 3D Model from Media Library</span>
                </button>
              )}
              <input
                className="sm-form-input"
                value={form.href}
                onChange={(e) => update('href', e.target.value)}
                placeholder="Or enter legacy cadwork path: assets/Models/.../viewer.html"
                style={{ marginTop: 6, fontSize: 11, color: '#999' }}
              />
            </div>

            <div className="sm-form-field full-width">
              <label className="sm-form-label">Features (one per line)</label>
              <textarea
                className="sm-form-textarea"
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder={"38x44 footprint\nMortice & Tenon Construction\nHalf Loft"}
                rows={5}
              />
            </div>
            <div className="sm-form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <button
                className={`sm-toggle ${form.onSale ? 'on' : ''}`}
                onClick={() => update('onSale', !form.onSale)}
              />
              <label className="sm-form-label" style={{ margin: 0, textTransform: 'none', fontSize: 13 }}>On Sale</label>
            </div>
            <div className="sm-form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <button
                className={`sm-toggle ${form.hidden ? 'on' : ''}`}
                onClick={() => update('hidden', !form.hidden)}
              />
              <label className="sm-form-label" style={{ margin: 0, textTransform: 'none', fontSize: 13 }}>Hidden from site</label>
            </div>
            <div className="sm-form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <button
                className={`sm-toggle ${form.featured ? 'on' : ''}`}
                onClick={() => update('featured', !form.featured)}
              />
              <label className="sm-form-label" style={{ margin: 0, textTransform: 'none', fontSize: 13 }}>Featured</label>
            </div>
          </div>
        </div>
        <div className="sm-modal-footer">
          <button className="sm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="sm-btn-primary" onClick={handleSubmit} disabled={saving || !form.name}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? 'Create Kit' : 'Save Changes'}
          </button>
        </div>
      </div>

      <MediaPicker isOpen={showThumbPicker} onClose={() => setShowThumbPicker(false)} onSelect={handleThumbSelect} allowedTypes={['image']} title="Select Thumbnail" />
      <MediaPicker isOpen={showGalleryPicker} onClose={() => setShowGalleryPicker(false)} onSelectMultiple={handleGalleryAddMultiple} allowMultiple allowedTypes={['image']} title="Add Gallery Images" />
      <MediaPicker isOpen={showModelPicker} onClose={() => setShowModelPicker(false)} onSelect={handleModelSelect} allowedTypes={['model']} title="Select 3D Model" />
    </div>
  );
};

// ─── Hero Tab ───────────────────────────────────────────────────────────────

interface HeroTabProps {
  hero: ShopHeroConfig;
  setHero: React.Dispatch<React.SetStateAction<ShopHeroConfig>>;
  onSave: () => void;
  saving: boolean;
}

const HeroTab: React.FC<HeroTabProps> = ({ hero, setHero, onSave, saving }) => {
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [showPosterPicker, setShowPosterPicker] = useState(false);
  const [showHowItWorksVideoPicker, setShowHowItWorksVideoPicker] = useState(false);

  const update = (field: keyof ShopHeroConfig, value: unknown) => {
    setHero((prev) => ({ ...prev, [field]: value }));
  };

  const handleVideoSelect = (item: MediaItem) => {
    update('droneVideoUrl', item.url);
    if (!hero.droneVideoPoster && item.thumbnailUrl) {
      update('droneVideoPoster', item.thumbnailUrl);
    }
  };

  const handlePosterSelect = (item: MediaItem) => {
    update('droneVideoPoster', item.url);
  };

  const handleHowItWorksVideoSelect = (item: MediaItem) => {
    update('howItWorksVideoUrl', item.url);
  };

  return (
    <>
      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Hero Background Video</h3>
        <p className="sm-form-section-desc">Drone footage plays behind the hero text and fades to the 3D model on scroll.</p>

        <div className="sm-media-row">
          <div className="sm-media-slot">
            <label className="sm-form-label"><Film size={13} /> Drone Video</label>
            {hero.droneVideoUrl ? (
              <div className="sm-media-preview">
                <video
                  src={hero.droneVideoUrl}
                  muted
                  loop
                  playsInline
                  autoPlay
                  preload="metadata"
                  className="sm-media-video"
                  onLoadedMetadata={(e) => {
                    const vid = e.currentTarget;
                    if (vid.paused) vid.currentTime = 0.5;
                  }}
                />
                <div className="sm-media-overlay">
                  <button className="sm-media-change-btn" onClick={() => setShowVideoPicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" onClick={() => update('droneVideoUrl', '')}>Remove</button>
                </div>
                <div className="sm-media-filename" title={hero.droneVideoUrl}>
                  {hero.droneVideoUrl.split('/').pop()?.split('?')[0] || 'Video'}
                </div>
              </div>
            ) : (
              <button className="sm-media-select-btn" onClick={() => setShowVideoPicker(true)}>
                <Film size={20} />
                <span>Select Video from Media Library</span>
              </button>
            )}
          </div>

          <div className="sm-media-slot">
            <label className="sm-form-label"><ImageIcon size={13} /> Poster Image (fallback)</label>
            {hero.droneVideoPoster ? (
              <div className="sm-media-preview">
                <img src={hero.droneVideoPoster} alt="Video poster" className="sm-media-image" />
                <div className="sm-media-overlay">
                  <button className="sm-media-change-btn" onClick={() => setShowPosterPicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" onClick={() => update('droneVideoPoster', '')}>Remove</button>
                </div>
              </div>
            ) : (
              <button className="sm-media-select-btn" onClick={() => setShowPosterPicker(true)}>
                <ImageIcon size={20} />
                <span>Select Poster Image</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">How It Works Video</h3>
        <p className="sm-form-section-desc">Video background for the &quot;How It Works&quot; timeline section. Should illustrate the process (CNC cutting, shipping, assembly).</p>
        <div className="sm-media-row">
          <div className="sm-media-slot">
            <label className="sm-form-label"><Film size={13} /> How It Works Video</label>
            {hero.howItWorksVideoUrl ? (
              <div className="sm-media-preview">
                <video
                  src={hero.howItWorksVideoUrl}
                  muted
                  loop
                  playsInline
                  autoPlay
                  preload="metadata"
                  className="sm-media-video"
                  onLoadedMetadata={(e) => {
                    const vid = e.currentTarget;
                    if (vid.paused) vid.currentTime = 0.5;
                  }}
                />
                <div className="sm-media-overlay">
                  <button className="sm-media-change-btn" onClick={() => setShowHowItWorksVideoPicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" onClick={() => update('howItWorksVideoUrl', '')}>Remove</button>
                </div>
                <div className="sm-media-filename" title={hero.howItWorksVideoUrl}>
                  {hero.howItWorksVideoUrl.split('/').pop()?.split('?')[0] || 'Video'}
                </div>
              </div>
            ) : (
              <button className="sm-media-select-btn" onClick={() => setShowHowItWorksVideoPicker(true)}>
                <Film size={20} />
                <span>Select Video from Media Library</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Headline & Tagline</h3>
        <p className="sm-form-section-desc">Controls the main heading text in the hero section.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field">
            <label className="sm-form-label">Tagline (above headline)</label>
            <input className="sm-form-input" value={hero.tagline} onChange={(e) => update('tagline', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Headline (light text)</label>
            <input className="sm-form-input" value={hero.headline} onChange={(e) => update('headline', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Headline Bold Part</label>
            <input className="sm-form-input" value={hero.headlineBold} onChange={(e) => update('headlineBold', e.target.value)} />
          </div>
          <div className="sm-form-field full-width">
            <label className="sm-form-label">Subheadline</label>
            <textarea className="sm-form-textarea" value={hero.subheadline} onChange={(e) => update('subheadline', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Call-to-Action Buttons</h3>
        <p className="sm-form-section-desc">The two CTA buttons shown below the headline.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field">
            <label className="sm-form-label">Primary CTA Label</label>
            <input className="sm-form-input" value={hero.cta1Label} onChange={(e) => update('cta1Label', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Primary CTA Link</label>
            <input className="sm-form-input" value={hero.cta1Link} onChange={(e) => update('cta1Link', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Secondary CTA Label</label>
            <input className="sm-form-input" value={hero.cta2Label} onChange={(e) => update('cta2Label', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Secondary CTA Link</label>
            <input className="sm-form-input" value={hero.cta2Link} onChange={(e) => update('cta2Link', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">3D Model</h3>
        <p className="sm-form-section-desc">The GLB model displayed in the hero section background.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field">
            <label className="sm-form-label">Model Path</label>
            <input className="sm-form-input" value={hero.modelPath} onChange={(e) => update('modelPath', e.target.value)} placeholder="/models/bent.glb" />
          </div>
          <div className="sm-form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <button className={`sm-toggle ${hero.showJoineryDetails ? 'on' : ''}`} onClick={() => update('showJoineryDetails', !hero.showJoineryDetails)} />
            <label className="sm-form-label" style={{ margin: 0, textTransform: 'none', fontSize: 13 }}>Show Joinery Details (Dovetail, Mortise &amp; Tenon, Trunnel Pegs)</label>
          </div>
        </div>
      </div>

      <div className="sm-form-actions">
        <button className="sm-btn-primary" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Hero
        </button>
      </div>

      <MediaPicker
        isOpen={showVideoPicker}
        onClose={() => setShowVideoPicker(false)}
        onSelect={handleVideoSelect}
        allowedTypes={['video']}
        title="Select Drone Video"
      />

      <MediaPicker
        isOpen={showPosterPicker}
        onClose={() => setShowPosterPicker(false)}
        onSelect={handlePosterSelect}
        allowedTypes={['image']}
        title="Select Poster Image"
      />

      <MediaPicker
        isOpen={showHowItWorksVideoPicker}
        onClose={() => setShowHowItWorksVideoPicker(false)}
        onSelect={handleHowItWorksVideoSelect}
        allowedTypes={['video']}
        title="Select How It Works Video"
      />
    </>
  );
};

// ─── Settings Tab ───────────────────────────────────────────────────────────

interface SettingsTabProps {
  settings: ShopSiteSettings;
  setSettings: React.Dispatch<React.SetStateAction<ShopSiteSettings>>;
  onSave: () => void;
  saving: boolean;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ settings, setSettings, onSave, saving }) => {
  const [newKeyword, setNewKeyword] = useState('');
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showOgImagePicker, setShowOgImagePicker] = useState(false);
  const [showFaviconPicker, setShowFaviconPicker] = useState(false);

  const update = (field: keyof ShopSiteSettings, value: unknown) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoSelect = (item: MediaItem) => {
    update('logoUrl', item.url);
  };

  const handleOgImageSelect = (item: MediaItem) => {
    update('ogImageUrl', item.url);
  };

  const handleFaviconSelect = (item: MediaItem) => {
    update('faviconUrl', item.url);
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !settings.seoKeywords.includes(newKeyword.trim())) {
      update('seoKeywords', [...settings.seoKeywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (kw: string) => {
    update('seoKeywords', settings.seoKeywords.filter((k) => k !== kw));
  };

  return (
    <>
      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Site Logo</h3>
        <p className="sm-form-section-desc">The logo displayed in the navigation bar and footer.</p>
        <div className="sm-media-row" style={{ gridTemplateColumns: '240px 1fr' }}>
          <div className="sm-media-slot">
            {settings.logoUrl ? (
              <div className="sm-media-preview sm-logo-preview sm-checkerboard">
                <img src={settings.logoUrl} alt="Site logo" className="sm-media-image" style={{ objectFit: 'contain', padding: 12 }} />
                <div className="sm-media-overlay">
                  <button className="sm-media-change-btn" onClick={() => setShowLogoPicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" onClick={() => update('logoUrl', '')}>Remove</button>
                </div>
              </div>
            ) : (
              <button className="sm-media-select-btn" onClick={() => setShowLogoPicker(true)}>
                <ImageIcon size={20} />
                <span>Select Logo</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
              Upload a transparent PNG or SVG for best results. The logo appears in the site header and footer.
            </p>
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Link Preview Image</h3>
        <p className="sm-form-section-desc">Image shown when sharing the site link (iMessage, social media). Uses the Site Logo above if empty. Use a square or 1200×630 image for best results.</p>
        <div className="sm-media-row" style={{ gridTemplateColumns: '240px 1fr' }}>
          <div className="sm-media-slot">
            {settings.ogImageUrl ? (
              <div className="sm-media-preview sm-checkerboard">
                <img src={settings.ogImageUrl} alt="Link preview" className="sm-media-image" style={{ objectFit: 'contain', padding: 12 }} />
                <div className="sm-media-overlay">
                  <button className="sm-media-change-btn" onClick={() => setShowOgImagePicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" onClick={() => update('ogImageUrl', '')}>Remove</button>
                </div>
              </div>
            ) : (
              <button className="sm-media-select-btn" onClick={() => setShowOgImagePicker(true)}>
                <ImageIcon size={20} />
                <span>Select Image</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
              Leave empty to use the Site Logo. Set a dedicated image here to keep the kits brand separate from other brands.
            </p>
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Sale Parameters</h3>
        <p className="sm-form-section-desc">Set a site-wide sale end date and time. Sale countdown banners will appear on product cards, product pages, and checkout when a sale is active.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field">
            <label className="sm-form-label">Sale End Date</label>
            <input
              className="sm-form-input"
              type="date"
              value={settings.saleEndDate || ''}
              onChange={(e) => update('saleEndDate', e.target.value)}
              placeholder="YYYY-MM-DD"
            />
            <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Leave empty to hide sale banners.</p>
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Sale End Time (24h)</label>
            <input
              className="sm-form-input"
              type="time"
              value={settings.saleEndTime || '23:59'}
              onChange={(e) => update('saleEndTime', e.target.value)}
            />
            <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Time on the end date when the sale expires.</p>
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Favicon</h3>
        <p className="sm-form-section-desc">The small icon shown in browser tabs and bookmarks. Recommended: square image, 32×32 or 64×64 px.</p>
        <div className="sm-media-row" style={{ gridTemplateColumns: '240px 1fr' }}>
          <div className="sm-media-slot">
            {settings.faviconUrl ? (
              <div className="sm-media-preview sm-checkerboard">
                <img src={settings.faviconUrl} alt="Favicon" className="sm-media-image" style={{ objectFit: 'contain', padding: 12 }} />
                <div className="sm-media-overlay">
                  <button className="sm-media-change-btn" onClick={() => setShowFaviconPicker(true)}>Change</button>
                  <button className="sm-media-remove-btn" onClick={() => update('faviconUrl', '')}>Remove</button>
                </div>
              </div>
            ) : (
              <button className="sm-media-select-btn" onClick={() => setShowFaviconPicker(true)}>
                <ImageIcon size={20} />
                <span>Select Favicon</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
              If empty, the default favicon is used. PNG or ICO work best.
            </p>
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Contact Information</h3>
        <p className="sm-form-section-desc">Displayed in the footer and used for customer inquiries.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field">
            <label className="sm-form-label">Email</label>
            <input className="sm-form-input" value={settings.contactEmail} onChange={(e) => update('contactEmail', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Phone</label>
            <input className="sm-form-input" value={settings.contactPhone} onChange={(e) => update('contactPhone', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Location</label>
            <input className="sm-form-input" value={settings.contactLocation} onChange={(e) => update('contactLocation', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">SEO</h3>
        <p className="sm-form-section-desc">Search engine optimization settings for the site.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field full-width">
            <label className="sm-form-label">Page Title</label>
            <input className="sm-form-input" value={settings.seoTitle} onChange={(e) => update('seoTitle', e.target.value)} />
          </div>
          <div className="sm-form-field full-width">
            <label className="sm-form-label">Meta Description</label>
            <textarea className="sm-form-textarea" value={settings.seoDescription} onChange={(e) => update('seoDescription', e.target.value)} />
          </div>
          <div className="sm-form-field full-width">
            <label className="sm-form-label">SEO Keywords</label>
            <div className="sm-tag-list" style={{ marginBottom: 8 }}>
              {settings.seoKeywords.map((kw) => (
                <span key={kw} className="sm-tag">
                  {kw}
                  <button onClick={() => removeKeyword(kw)}><X size={12} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="sm-form-input"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
                placeholder="Add keyword..."
                style={{ flex: 1 }}
              />
              <button className="sm-btn-secondary" onClick={addKeyword} disabled={!newKeyword.trim()}>
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Links & URLs</h3>
        <p className="sm-form-section-desc">External URLs referenced by the site.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field">
            <label className="sm-form-label">Custom Design Price</label>
            <input className="sm-form-input" value={settings.customDesignPrice} onChange={(e) => update('customDesignPrice', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Custom Design URL</label>
            <input className="sm-form-input" value={settings.customDesignUrl} onChange={(e) => update('customDesignUrl', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Claim Kit URL</label>
            <input className="sm-form-input" value={settings.claimKitUrl} onChange={(e) => update('claimKitUrl', e.target.value)} />
          </div>
          <div className="sm-form-field">
            <label className="sm-form-label">Builder Login URL</label>
            <input className="sm-form-input" value={settings.footerBuilderLoginUrl} onChange={(e) => update('footerBuilderLoginUrl', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="sm-form-section">
        <h3 className="sm-form-section-title">Payments</h3>
        <p className="sm-form-section-desc">Stripe integration for checkout. The publishable key is safe to store here — it is a public key.</p>
        <div className="sm-form-grid">
          <div className="sm-form-field full-width">
            <label className="sm-form-label">Stripe Publishable Key</label>
            <input
              className="sm-form-input"
              value={settings.stripePublishableKey}
              onChange={(e) => update('stripePublishableKey', e.target.value)}
              placeholder="pk_live_... or pk_test_..."
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        </div>
      </div>

      <div className="sm-form-actions">
        <button className="sm-btn-primary" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Settings
        </button>
      </div>

      <MediaPicker
        isOpen={showLogoPicker}
        onClose={() => setShowLogoPicker(false)}
        onSelect={handleLogoSelect}
        allowedTypes={['image']}
        title="Select Logo"
      />
      <MediaPicker
        isOpen={showOgImagePicker}
        onClose={() => setShowOgImagePicker(false)}
        onSelect={handleOgImageSelect}
        allowedTypes={['image']}
        title="Select Link Preview Image"
      />
      <MediaPicker
        isOpen={showFaviconPicker}
        onClose={() => setShowFaviconPicker(false)}
        onSelect={handleFaviconSelect}
        allowedTypes={['image']}
        title="Select Favicon"
      />
    </>
  );
};

// ─── Testimonials Tab ────────────────────────────────────────────────────────

interface TestimonialsTabProps {
  testimonials: ShopTestimonial[];
  kits: ShopKitModel[];
  saving: boolean;
  setSaving: (v: boolean) => void;
  flash: (msg: string) => void;
}

const TestimonialsTab: React.FC<TestimonialsTabProps> = ({ testimonials, kits, saving, setSaving, flash }) => {
  const [editing, setEditing] = useState<ShopTestimonial | null>(null);
  const [isNew, setIsNew] = useState(false);

  const handleNew = () => {
    setIsNew(true);
    setEditing({
      ...DEFAULT_TESTIMONIAL,
      id: '',
      sortOrder: testimonials.length,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as ShopTestimonial);
  };

  const handleSave = async (t: ShopTestimonial) => {
    setSaving(true);
    try {
      if (isNew) {
        const { id, createdAt, updatedAt, ...rest } = t;
        await createTestimonial(rest);
        flash('Testimonial created');
      } else {
        await saveTestimonial(t);
        flash('Testimonial updated');
      }
      setEditing(null);
      setIsNew(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await deleteTestimonial(id);
    flash('Testimonial deleted');
  };

  return (
    <>
      <div className="sm-catalog-toolbar" style={{ marginBottom: 16 }}>
        <div className="sm-catalog-toolbar-left">
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {testimonials.length} testimonial{testimonials.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="sm-catalog-toolbar-right">
          <button className="sm-btn-primary" onClick={handleNew}>
            <Plus size={14} /> Add Testimonial
          </button>
        </div>
      </div>

      {testimonials.length === 0 ? (
        <div className="sm-empty">
          <MessageSquareQuote size={48} />
          <p>No testimonials yet. Add your first customer testimonial.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {testimonials.map((t) => (
            <div
              key={t.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '14px 18px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
              }}
            >
              <GripVertical size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0, cursor: 'grab' }} />
              {t.photoUrl ? (
                <img
                  src={t.photoUrl}
                  alt={t.name}
                  style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: 'var(--color-background)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 18,
                  }}
                >
                  {t.name ? t.name.charAt(0).toUpperCase() : '?'}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{t.name || 'Unnamed'}</span>
                  {t.location && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t.location}</span>}
                  <span className={`sm-badge sm-badge-${t.status}`} style={{ fontSize: 10 }}>{t.status}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  "{t.quote}"
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <div style={{ display: 'flex', gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={12}
                        fill={s <= t.rating ? '#f59e0b' : 'none'}
                        stroke={s <= t.rating ? '#f59e0b' : '#d1d5db'}
                      />
                    ))}
                  </div>
                  {t.kitName && (
                    <span className="sm-badge" style={{ background: 'var(--color-background)', fontSize: 10 }}>
                      {t.kitName}
                    </span>
                  )}
                </div>
              </div>
              <div className="sm-kit-actions">
                <button className="sm-icon-btn" title="Edit" onClick={() => { setEditing(t); setIsNew(false); }}>
                  <Pencil size={15} />
                </button>
                <button className="sm-icon-btn danger" title="Delete" onClick={() => handleDelete(t.id)}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TestimonialEditorModal
          testimonial={editing}
          isNew={isNew}
          saving={saving}
          kits={kits}
          onSave={handleSave}
          onClose={() => { setEditing(null); setIsNew(false); }}
        />
      )}
    </>
  );
};

// ─── Testimonial Editor Modal ────────────────────────────────────────────────

interface TestimonialEditorModalProps {
  testimonial: ShopTestimonial;
  isNew: boolean;
  saving: boolean;
  kits: ShopKitModel[];
  onSave: (t: ShopTestimonial) => void;
  onClose: () => void;
}

const TestimonialEditorModal: React.FC<TestimonialEditorModalProps> = ({
  testimonial, isNew, saving, kits, onSave, onClose,
}) => {
  const [form, setForm] = useState<ShopTestimonial>({ ...testimonial });
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  const update = (field: keyof ShopTestimonial, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoSelect = (item: MediaItem) => {
    update('photoUrl', item.url);
  };

  const handleKitChange = (kitId: string) => {
    if (!kitId) {
      update('kitId', '');
      update('kitName', '');
      return;
    }
    const kit = kits.find((k) => k.id === kitId);
    setForm((prev) => ({ ...prev, kitId, kitName: kit?.name || '' }));
  };

  return (
    <div className="sm-modal-overlay" onClick={onClose}>
      <div className="sm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="sm-modal-header">
          <h2>{isNew ? 'Add Testimonial' : `Edit: ${form.name || 'Testimonial'}`}</h2>
          <button className="sm-icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sm-modal-body">
          <div className="sm-form-grid">
            {/* Photo */}
            <div className="sm-form-field full-width" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {form.photoUrl ? (
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={form.photoUrl}
                    alt={form.name}
                    style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }}
                  />
                  <button
                    onClick={() => update('photoUrl', '')}
                    style={{
                      position: 'absolute', top: -4, right: -4,
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'var(--color-danger, #ef4444)', color: '#fff',
                      border: 'none', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 10,
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowPhotoPicker(true)}
                  style={{
                    width: 72, height: 72, borderRadius: '50%',
                    border: '2px dashed var(--color-border)',
                    background: 'var(--color-background)',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 2,
                    color: 'var(--color-text-muted)', fontSize: 10, flexShrink: 0,
                  }}
                >
                  <ImageIcon size={16} />
                  Photo
                </button>
              )}
              <div style={{ flex: 1 }}>
                <div className="sm-form-field" style={{ marginBottom: 8 }}>
                  <label className="sm-form-label">Name</label>
                  <input
                    className="sm-form-input"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
                <div className="sm-form-field">
                  <label className="sm-form-label">Location</label>
                  <input
                    className="sm-form-input"
                    value={form.location}
                    onChange={(e) => update('location', e.target.value)}
                    placeholder="Nashville, TN"
                  />
                </div>
              </div>
            </div>

            {/* Quote */}
            <div className="sm-form-field full-width">
              <label className="sm-form-label">Quote</label>
              <textarea
                className="sm-form-textarea"
                value={form.quote}
                onChange={(e) => update('quote', e.target.value)}
                placeholder="What the customer said about their experience..."
                rows={4}
              />
            </div>

            {/* Rating */}
            <div className="sm-form-field">
              <label className="sm-form-label">Rating</label>
              <div style={{ display: 'flex', gap: 4, paddingTop: 4 }}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    onClick={() => update('rating', s)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 2, lineHeight: 1,
                    }}
                  >
                    <Star
                      size={22}
                      fill={s <= form.rating ? '#f59e0b' : 'none'}
                      stroke={s <= form.rating ? '#f59e0b' : '#d1d5db'}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Kit Selector */}
            <div className="sm-form-field">
              <label className="sm-form-label">Kit Purchased</label>
              <select
                className="sm-form-select"
                value={form.kitId || ''}
                onChange={(e) => handleKitChange(e.target.value)}
              >
                <option value="">None</option>
                {kits
                  .filter((k) => k.status === 'active')
                  .map((k) => (
                    <option key={k.id} value={k.id}>{k.name}</option>
                  ))}
              </select>
            </div>

            {/* Status */}
            <div className="sm-form-field">
              <label className="sm-form-label">Status</label>
              <select
                className="sm-form-select"
                value={form.status}
                onChange={(e) => update('status', e.target.value as 'active' | 'draft')}
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="sm-form-field">
              <label className="sm-form-label">Sort Order</label>
              <input
                className="sm-form-input"
                type="number"
                value={form.sortOrder}
                onChange={(e) => update('sortOrder', parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>
        </div>
        <div className="sm-modal-footer">
          <button className="sm-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="sm-btn-primary"
            onClick={() => onSave(form)}
            disabled={saving || !form.name || !form.quote}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      <MediaPicker
        isOpen={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={handlePhotoSelect}
        allowedTypes={['image']}
        title="Select Customer Photo"
      />
    </div>
  );
};

// ─── Orders Tab (Placeholder) ───────────────────────────────────────────────

const OrdersTab: React.FC = () => (
  <div className="sm-orders-placeholder">
    <ShoppingCart size={64} />
    <h3>Order Management</h3>
    <p>
      When checkout is enabled on shoptimberframekits.com, orders will appear here.
      You'll be able to track orders, manage fulfillment, and view revenue.
    </p>
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      <span className="sm-badge sm-badge-draft">Coming Soon</span>
    </div>
  </div>
);

export default ShopManagementPage;
